import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { PortalClass } from '../../types/portal';
import { Plus, Edit2, Archive, RotateCcw, X, Check, Upload, ImageIcon, Loader2, LayoutGrid, List } from 'lucide-react';

const VIEW_KEY = 'arabicroots:admin-classes-view';

const cardClass =
  'bg-white rounded-[24px] border border-[#eadfd6] shadow-[0_10px_35px_rgba(0,0,0,0.05)]';
const inputClass =
  'w-full px-4 py-3 border border-[#ddd6cf] rounded-2xl focus:ring-2 focus:ring-[#970000]/10 focus:border-[#970000] outline-none transition-colors duration-200 bg-white';
const primaryBtn =
  'inline-flex items-center px-4 py-2 rounded-full bg-[#970000] text-white hover:bg-[#7a0000] transition-colors duration-200 font-medium text-sm';
const secondaryBtn =
  'inline-flex items-center px-4 py-2 rounded-full border border-[#d9c9bd] text-[#5f5148] hover:bg-[#f8f3ef] transition-colors duration-200 font-medium text-sm';

type FormState = Omit<PortalClass, 'id' | 'created_at' | 'updated_at' | 'archived_at'>;

const emptyForm = (): FormState => ({
  title: '',
  level: '',
  category: '',
  description: '',
  gender: null,
  schedule: '',
  discord_link: '',
  whatsapp_link: '',
  cover_image_url: null,
  is_active: true,
});

async function uploadCoverImage(file: File, classId: string): Promise<string> {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? 'jpg';
  const path = `${classId}.${ext}`;
  const { error } = await supabase.storage
    .from('class-covers')
    .upload(path, file, { upsert: true, contentType: file.type });
  if (error) throw error;
  const { data } = supabase.storage.from('class-covers').getPublicUrl(path);
  // Bust cache by appending timestamp
  return `${data.publicUrl}?t=${Date.now()}`;
}

const PortalClassesManager: React.FC = () => {
  const [classes, setClasses] = useState<PortalClass[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm());
  const [saving, setSaving] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [opError, setOpError] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>(() => {
    try {
      return localStorage.getItem(VIEW_KEY) === 'list' ? 'list' : 'grid';
    } catch {
      return 'grid';
    }
  });
  const fileInputRef = useRef<HTMLInputElement>(null);

  const switchView = (mode: 'grid' | 'list') => {
    setViewMode(mode);
    try { localStorage.setItem(VIEW_KEY, mode); } catch { /* egal */ }
  };

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    const { data } = await supabase.from('portal_classes').select('*').order('title');
    setClasses((data ?? []) as PortalClass[]);
    setLoading(false);
  };

  const visible = classes.filter(c =>
    showArchived ? c.archived_at !== null : c.archived_at === null
  );

  const openCreate = () => {
    setEditId(null);
    setForm(emptyForm());
    setImageFile(null);
    setImagePreview(null);
    setUploadError('');
    setShowForm(true);
  };

  const openEdit = (c: PortalClass) => {
    setEditId(c.id);
    setForm({
      title: c.title,
      level: c.level ?? '',
      category: c.category ?? '',
      description: c.description ?? '',
      gender: c.gender,
      schedule: c.schedule ?? '',
      discord_link: c.discord_link ?? '',
      whatsapp_link: c.whatsapp_link ?? '',
      cover_image_url: c.cover_image_url ?? null,
      is_active: c.is_active,
    });
    setImageFile(null);
    setImagePreview(c.cover_image_url ?? null);
    setUploadError('');
    setShowForm(true);
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Bild ist zu groß. Maximal 5 MB erlaubt.');
      return;
    }
    setUploadError('');
    setImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    setForm(prev => ({ ...prev, cover_image_url: null }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.title.trim()) return;
    setSaving(true);
    setUploadError('');
    try {
      let coverUrl: string | null = form.cover_image_url ?? null;

      // If new class: insert first to get ID, then upload image
      if (!editId) {
        const payload = buildPayload(form, coverUrl);
        const { data, error } = await supabase
          .from('portal_classes')
          .insert(payload)
          .select()
          .single();
        if (error) throw error;
        const newClass = data as PortalClass;

        if (imageFile) {
          try {
            coverUrl = await uploadCoverImage(imageFile, newClass.id);
            await supabase
              .from('portal_classes')
              .update({ cover_image_url: coverUrl, updated_at: new Date().toISOString() })
              .eq('id', newClass.id);
          } catch {
            setUploadError('Klasse erstellt, aber Bild-Upload fehlgeschlagen. Bitte erneut bearbeiten.');
          }
        }
        setClasses(prev => [{ ...newClass, cover_image_url: coverUrl } as PortalClass, ...prev]);
      } else {
        // Edit: upload image first (if new file selected), then update
        if (imageFile) {
          try {
            coverUrl = await uploadCoverImage(imageFile, editId);
          } catch {
            setUploadError('Bild-Upload fehlgeschlagen. Andere Änderungen wurden trotzdem gespeichert.');
          }
        }
        const payload = buildPayload(form, coverUrl);
        const { error } = await supabase
          .from('portal_classes')
          .update(payload)
          .eq('id', editId);
        if (error) throw error;
        setClasses(prev =>
          prev.map(c => (c.id === editId ? { ...c, ...payload, cover_image_url: coverUrl } : c))
        );
      }

      setShowForm(false);
    } catch (err) {
      if (import.meta.env.DEV) console.error(err);
      setOpError('Fehler beim Speichern. Bitte erneut versuchen.');
    } finally {
      setSaving(false);
    }
  };

  const toggleArchive = async (c: PortalClass) => {
    const archived_at = c.archived_at ? null : new Date().toISOString();
    const { error } = await supabase
      .from('portal_classes')
      .update({ archived_at, updated_at: new Date().toISOString() })
      .eq('id', c.id);
    if (!error) {
      setClasses(prev => prev.map(cl => (cl.id === c.id ? { ...cl, archived_at } : cl)));
    }
  };

  const f = (field: keyof FormState) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-sm font-semibold tracking-[0.12em] uppercase text-[#970000]/70 mb-1">Portal</p>
          <h2 className="text-3xl font-bold text-[#1f1f1f]">Klassen</h2>
        </div>
        <div className="flex gap-2 items-center">
          <div className="inline-flex rounded-full border border-[#d9c9bd] p-0.5 bg-white">
            <button
              onClick={() => switchView('grid')}
              className={`p-2 rounded-full transition-colors ${viewMode === 'grid' ? 'bg-[#970000] text-white' : 'text-[#6b6b6b] hover:bg-[#f8f3ef]'}`}
              title="Kartenansicht"
            >
              <LayoutGrid className="h-4 w-4" />
            </button>
            <button
              onClick={() => switchView('list')}
              className={`p-2 rounded-full transition-colors ${viewMode === 'list' ? 'bg-[#970000] text-white' : 'text-[#6b6b6b] hover:bg-[#f8f3ef]'}`}
              title="Listenansicht"
            >
              <List className="h-4 w-4" />
            </button>
          </div>
          <button onClick={() => setShowArchived(p => !p)} className={secondaryBtn}>
            {showArchived ? 'Aktive' : 'Archivierte'}
          </button>
          <button onClick={openCreate} className={primaryBtn}>
            <Plus className="h-4 w-4 mr-1" />
            Neue Klasse
          </button>
        </div>
      </div>

      {opError && (
        <div className="bg-red-50 border border-red-200 text-red-700 rounded-2xl px-4 py-3 text-sm flex items-center justify-between">
          <span>{opError}</span>
          <button onClick={() => setOpError('')} className="ml-4 text-red-500 hover:text-red-700">✕</button>
        </div>
      )}

      {/* Form modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-[24px] w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#eadfd6]">
              <h3 className="text-lg font-bold text-[#1f1f1f]">
                {editId ? 'Klasse bearbeiten' : 'Neue Klasse'}
              </h3>
              <button onClick={() => setShowForm(false)} className="p-2 rounded-xl hover:bg-[#f8f3ef]">
                <X className="h-5 w-5 text-[#6b6b6b]" />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4">

              {/* Cover image */}
              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">
                  Cover-Bild
                </label>
                {imagePreview ? (
                  <div className="relative w-full h-40 rounded-2xl overflow-hidden border border-[#ddd6cf] group">
                    <img
                      src={imagePreview}
                      alt="Cover-Vorschau"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center gap-2 opacity-0 group-hover:opacity-100">
                      <button
                        type="button"
                        onClick={() => fileInputRef.current?.click()}
                        className="bg-white text-[#1f1f1f] text-xs font-semibold px-3 py-1.5 rounded-xl flex items-center gap-1.5"
                      >
                        <Upload className="h-3.5 w-3.5" />
                        Ändern
                      </button>
                      <button
                        type="button"
                        onClick={removeImage}
                        className="bg-red-600 text-white text-xs font-semibold px-3 py-1.5 rounded-xl flex items-center gap-1.5"
                      >
                        <X className="h-3.5 w-3.5" />
                        Entfernen
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-32 rounded-2xl border-2 border-dashed border-[#ddd6cf] flex flex-col items-center justify-center gap-2 text-[#9b9490] hover:border-[#970000] hover:text-[#970000] transition-colors"
                  >
                    <ImageIcon className="h-8 w-8" />
                    <span className="text-sm font-medium">Bild auswählen</span>
                    <span className="text-xs">JPG, PNG, WebP – max. 5 MB</span>
                  </button>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleImageChange}
                  className="hidden"
                />
                {uploadError && (
                  <p className="text-xs text-red-600 mt-1.5">{uploadError}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Titel *</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={f('title')}
                  className={inputClass}
                  required
                  placeholder="z.B. Arabisch Level 1 – Gruppe A"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Level</label>
                  <input
                    type="text"
                    value={form.level ?? ''}
                    onChange={f('level')}
                    className={inputClass}
                    placeholder="z.B. Level 1"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Kategorie-Tag</label>
                  <input
                    type="text"
                    value={form.category ?? ''}
                    onChange={f('category')}
                    className={inputClass}
                    placeholder="z.B. ARABISCH"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Geschlecht</label>
                  <select
                    value={form.gender ?? ''}
                    onChange={e =>
                      setForm(prev => ({
                        ...prev,
                        gender: (e.target.value as PortalClass['gender']) || null,
                      }))
                    }
                    className={inputClass}
                  >
                    <option value="">Keine Angabe</option>
                    <option value="mixed">Gemischt</option>
                    <option value="male">Männlich</option>
                    <option value="female">Weiblich</option>
                    <option value="all">Alle</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Zeiten</label>
                  <input
                    type="text"
                    value={form.schedule ?? ''}
                    onChange={f('schedule')}
                    className={inputClass}
                    placeholder="z.B. Di. 19–20:30 Uhr"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Beschreibung</label>
                <textarea
                  value={form.description ?? ''}
                  onChange={f('description')}
                  className={inputClass + ' resize-none'}
                  rows={3}
                  placeholder="Kurzbeschreibung der Klasse"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">Discord-Link</label>
                <input
                  type="url"
                  value={form.discord_link ?? ''}
                  onChange={f('discord_link')}
                  className={inputClass}
                  placeholder="https://discord.gg/…"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-[#1f1f1f] mb-1.5">WhatsApp-Gruppe</label>
                <input
                  type="url"
                  value={form.whatsapp_link ?? ''}
                  onChange={f('whatsapp_link')}
                  className={inputClass}
                  placeholder="https://chat.whatsapp.com/…"
                />
              </div>

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={e => setForm(prev => ({ ...prev, is_active: e.target.checked }))}
                  className="rounded"
                />
                <span className="text-sm font-medium text-[#1f1f1f]">Aktiv</span>
              </label>

              <div className="flex gap-3 pt-2">
                <button type="submit" disabled={saving} className={primaryBtn}>
                  {saving ? (
                    <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                  ) : (
                    <Check className="h-4 w-4 mr-1" />
                  )}
                  {saving ? 'Speichert…' : 'Speichern'}
                </button>
                <button type="button" onClick={() => setShowForm(false)} className={secondaryBtn}>
                  Abbrechen
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Klassen-Übersicht */}
      {loading ? (
        <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="animate-pulse bg-white rounded-[24px] border border-[#eadfd6] h-40" />
          ))}
        </div>
      ) : visible.length === 0 ? (
        <div className={cardClass + ' p-10 text-center text-[#6b6b6b]'}>
          Keine {showArchived ? 'archivierten' : 'aktiven'} Klassen gefunden.
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {visible.map(c => (
            <div key={c.id} className={cardClass + ' overflow-hidden flex flex-col'}>
              {/* Cover image */}
              {c.cover_image_url ? (
                <div className="h-24 bg-[#f8f3ef] overflow-hidden">
                  <img
                    src={c.cover_image_url}
                    alt={c.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              ) : (
                <div className="h-14 bg-gradient-to-br from-[#1f1208] to-[#3d2510] flex items-center justify-center">
                  <ImageIcon className="h-5 w-5 text-white/20" />
                </div>
              )}
              <div className="p-4 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-2 mb-1.5">
                  <h3 className="font-bold text-[#1f1f1f] text-sm leading-snug">{c.title}</h3>
                  <div className="flex gap-0.5 shrink-0">
                    <button
                      onClick={() => openEdit(c)}
                      className="p-1.5 rounded-lg text-[#6b6b6b] hover:bg-[#f8f3ef]"
                      title="Bearbeiten"
                    >
                      <Edit2 className="h-3.5 w-3.5" />
                    </button>
                    <button
                      onClick={() => toggleArchive(c)}
                      className="p-1.5 rounded-lg text-[#6b6b6b] hover:bg-[#f8f3ef]"
                      title={c.archived_at ? 'Wiederherstellen' : 'Archivieren'}
                    >
                      {c.archived_at ? (
                        <RotateCcw className="h-3.5 w-3.5 text-green-600" />
                      ) : (
                        <Archive className="h-3.5 w-3.5" />
                      )}
                    </button>
                  </div>
                </div>
                <div className="flex flex-wrap gap-1 mb-1.5">
                  {c.level && (
                    <span className="text-[11px] bg-[#f8f3ef] text-[#5f5148] px-2 py-0.5 rounded-full">
                      {c.level}
                    </span>
                  )}
                  {c.category && (
                    <span className="text-[11px] bg-[#1f1f1f] text-white px-2 py-0.5 rounded-full">
                      {c.category}
                    </span>
                  )}
                </div>
                {c.schedule && (
                  <p className="text-[11px] text-[#9b9490] mt-auto pt-1">{c.schedule}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* Listenansicht */
        <div className={cardClass + ' overflow-hidden'}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[#eadfd6]">
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Klasse</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Level</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Zeiten</th>
                  <th className="text-left p-4 font-semibold text-[#6b6b6b]">Status</th>
                  <th className="text-right p-4 font-semibold text-[#6b6b6b]">Aktionen</th>
                </tr>
              </thead>
              <tbody>
                {visible.map(c => (
                  <tr key={c.id} className="border-b border-[#eadfd6] last:border-b-0 hover:bg-[#faf8f6]">
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {c.cover_image_url ? (
                          <img
                            src={c.cover_image_url}
                            alt=""
                            className="h-9 w-14 rounded-lg object-cover border border-[#eadfd6] shrink-0"
                          />
                        ) : (
                          <div className="h-9 w-14 rounded-lg bg-gradient-to-br from-[#1f1208] to-[#3d2510] flex items-center justify-center shrink-0">
                            <ImageIcon className="h-3.5 w-3.5 text-white/20" />
                          </div>
                        )}
                        <div>
                          <p className="font-medium text-[#1f1f1f]">{c.title}</p>
                          {c.category && (
                            <p className="text-[11px] text-[#9b9490] uppercase tracking-wide">{c.category}</p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="p-4 text-[#6b6b6b]">{c.level || '–'}</td>
                    <td className="p-4 text-[#6b6b6b]">{c.schedule || '–'}</td>
                    <td className="p-4">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                        c.archived_at
                          ? 'bg-[#f5f0ea] text-[#8a8178]'
                          : c.is_active
                            ? 'bg-green-100 text-green-800'
                            : 'bg-amber-100 text-amber-800'
                      }`}>
                        {c.archived_at ? 'Archiviert' : c.is_active ? 'Aktiv' : 'Inaktiv'}
                      </span>
                    </td>
                    <td className="p-4">
                      <div className="flex gap-1 justify-end">
                        <button
                          onClick={() => openEdit(c)}
                          className="p-2 rounded-xl text-[#6b6b6b] hover:bg-[#f8f3ef]"
                          title="Bearbeiten"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => toggleArchive(c)}
                          className="p-2 rounded-xl text-[#6b6b6b] hover:bg-[#f8f3ef]"
                          title={c.archived_at ? 'Wiederherstellen' : 'Archivieren'}
                        >
                          {c.archived_at ? (
                            <RotateCcw className="h-4 w-4 text-green-600" />
                          ) : (
                            <Archive className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

function buildPayload(form: FormState, coverUrl: string | null) {
  return {
    title: form.title.trim(),
    level: form.level?.trim() || null,
    category: form.category?.trim() || null,
    description: form.description?.trim() || null,
    gender: form.gender || null,
    schedule: form.schedule?.trim() || null,
    discord_link: form.discord_link?.trim() || null,
    whatsapp_link: form.whatsapp_link?.trim() || null,
    cover_image_url: coverUrl,
    is_active: form.is_active,
    updated_at: new Date().toISOString(),
  };
}

export default PortalClassesManager;
